import { FC, useContext, useMemo } from 'react';
import { formatNumber } from '@/utils';
import { buildHcoBk } from '@/utils/hcoUtils';

import { useGetHcpTotalCount } from '@/api/queries/hcp/useGetHcpTotalCount';
import { LoadingSpinnerFallback } from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import { StethoscopeIcon } from '@components/base/Icons/RocheIconPack';
import { Filter } from '@cubejs-client/core';
import { useTranslation } from 'react-i18next';
import styles from './NumberSelectedHcps.module.scss';
import { useGetStructureSummaryHcpCount } from '@/api/hco/structureSummary/useGetStructureSummaryCounts';
import { useLocation, useParams } from 'react-router-dom';
import { useHcoDataScope } from '@/pages/HCOOverview/hooks/useHcoDataScope';
import { isHcoOverviewPath } from '../NavigationBar/routeExclusions';
import Text from '@/storybook/components/Text/Text';
import FiltersContext from '@/context/FiltersContext/FiltersContext';

interface NumberSelectedHcpsProps {
  tierFilter?: Filter;
  ppocFilter?: Filter;
}

const NumberSelectedHcps: FC<NumberSelectedHcpsProps> = ({ tierFilter, ppocFilter }) => {
  const { activeFilters } = useContext(FiltersContext);
  const { t } = useTranslation();
  const { id, countryCode } = useParams();
  const location = useLocation();
  const additionalFilters = useMemo(() => {
    let filters = [];
    if (tierFilter) {
      filters.push(tierFilter);
    }
    if (ppocFilter) {
      filters.push(ppocFilter);
    }
    return filters;
  }, [tierFilter, ppocFilter]);
  const globalFilterApplied = activeFilters.length > 0 || additionalFilters.length > 0;
  const isHcoOverview = isHcoOverviewPath(location.pathname);
  const hcoBk = isHcoOverview ? buildHcoBk(countryCode, id) : undefined;
  const { includeAllDepartments } = useHcoDataScope(hcoBk);
  const { hcp, isLoading: isHcoCountLoading } = useGetStructureSummaryHcpCount(hcoBk, includeAllDepartments);

  const { data: totalRecordsWithFilters = 0, isLoading: isTotalRecordsWithFiltersLoading } = useGetHcpTotalCount({
    additionalFilters,
  });
  const { data: totalRecordsNoFilters = 0 } = useGetHcpTotalCount({
    hcpBks: [],
    options: {
      globalFilterApplied: false,
    },
  });

  const getDisplayedCount = (): string => {
    if (isHcoOverview) return formatNumber(hcp.data ?? 0, 0);
    if (globalFilterApplied) return formatNumber(totalRecordsWithFilters, 0);
    return formatNumber(totalRecordsNoFilters, 0);
  };

  const isCountLoading = (): boolean => {
    if (isHcoOverview) return isHcoCountLoading;
    return globalFilterApplied && isTotalRecordsWithFiltersLoading;
  };

  if (isCountLoading()) {
    return <LoadingSpinnerFallback size={16} />;
  }

  return (
    <div className={styles.numberHcps}>
      <div className={styles.countWrapper}>
        <StethoscopeIcon />
        <Text as="span" className={styles.filtered}>
          {getDisplayedCount()}
        </Text>
      </div>
      <span className={styles.hcpSelected}>{t('header.navigation.hcpsSelected')}</span>
    </div>
  );
};

export default NumberSelectedHcps;
